const FALLBACK_API_BASE_URL = (() => {
  if (typeof window === "undefined") {
    return "http://127.0.0.1:8000/api/v1";
  }
  const { hostname, origin } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://127.0.0.1:8000/api/v1";
  }
  return `${origin}/api/v1`;
})();

const API_BASE_URL = (() => {
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return FALLBACK_API_BASE_URL;
    }
  }
  return process.env.REACT_APP_API_BASE_URL || FALLBACK_API_BASE_URL;
})();


const BACKEND_UNREACHABLE_MSG =
  "Backend not reachable. Ensure frontend can access the API server at " + API_BASE_URL;

class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.data = options.data;
    this.fieldErrors = options.fieldErrors || {};
  }
}

function extractFieldErrors(detail) {
  if (!Array.isArray(detail)) return {};

  return detail.reduce((acc, item) => {
    if (!item || typeof item !== "object") return acc;

    const message = typeof item.msg === "string" ? item.msg : "";
    const loc = Array.isArray(item.loc) ? item.loc : [];
    const bodyIndex = loc.indexOf("body");
    const path = (bodyIndex >= 0 ? loc.slice(bodyIndex + 1) : loc)
      .filter((part) => typeof part === "string" || typeof part === "number")
      .join(".");

    if (path && message && !acc[path]) {
      acc[path] = message;
    }

    return acc;
  }, {});
}

function buildApiError(statusCode, data, fallbackMessage) {
  const fieldErrors = extractFieldErrors(data?.detail);
  const validationMessage = Object.values(fieldErrors)[0];
  const detailMessage =
    typeof data?.detail === "string"
      ? data.detail
      : typeof data?.error?.message === "string"
        ? data.error.message
        : validationMessage;

  return new ApiError(detailMessage || fallbackMessage || `Request failed: ${statusCode}`, {
    status: statusCode,
    data,
    fieldErrors,
  });
}

async function request(path, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(options.headers || {}),
  };

  let resp;
  try {
    resp = await fetch(url, { ...options, headers });
  } catch (e) {
    if (e.name === "TypeError" && e.message.includes("fetch")) {
      throw new Error(BACKEND_UNREACHABLE_MSG);
    }
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  const data = await resp.json().catch(() => null);

  if (!resp.ok) {
    throw buildApiError(resp.status, data, `Request failed: ${resp.status}`);
  }

  return data;
}

export async function apiLogin(email, password) {
  let resp;
  try {
    resp = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  const data = await resp.json().catch(() => null);
  if (!resp.ok || !data?.success) {
    throw buildApiError(resp.status, data, "Invalid email or password");
  }
  return data.data;
}

export async function getAdminDashboard(token) {
  return request("/admin/dashboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getRevenueReport(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.months) searchParams.append("months", String(params.months));
  const qs = searchParams.toString();
  return request(`/reports/revenue${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAgentReport(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.append("q", params.q);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/reports/agents${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getBankReport(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.append("q", params.q);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/reports/banks${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getLeads(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.append("status", params.status);
  if (params.q) searchParams.append("q", params.q);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/leads${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function bulkUpdateLeads(token, payload) {
  return request("/leads/bulk-update", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function createLead(token, payload) {
  return request("/leads", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateLead(token, leadId, payload) {
  return request(`/leads/${leadId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteLead(token, leadId) {
  return request(`/leads/${leadId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getLeadFollowUps(token, leadId) {
  return request(`/leads/${leadId}/follow-ups`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createLeadFollowUp(token, leadId, payload) {
  return request(`/leads/${leadId}/follow-ups`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateLeadFollowUp(token, followUpId, payload) {
  return request(`/follow-ups/${followUpId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function exportLeads(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.append("status", params.status);
  if (params.q) searchParams.append("q", params.q);
  if (params.format) searchParams.append("format", params.format);
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  const qs = searchParams.toString();
  const { blob, filename } = await downloadFile(`/leads/export${qs ? `?${qs}` : ""}`, token);
  triggerBrowserDownload(blob, filename);
}

export async function getAgentDashboard(token) {
  return request("/agent/dashboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getEmployeeDashboard(token) {
  return request("/employee/dashboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getWhiteLabelDashboard(token) {
  return request("/whitelabel/dashboard", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getLeadDetails(token, leadId) {
  return request(`/leads/${leadId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getFollowUps(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.bucket) searchParams.append("bucket", params.bucket);
  if (params.q) searchParams.append("q", params.q);
  if (params.status) searchParams.append("status", params.status);
  if (params.priority) searchParams.append("priority", params.priority);
  if (params.followUpType) searchParams.append("follow_up_type", params.followUpType);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/follow-ups${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getEmployees(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.append("status", params.status);
  if (params.q) searchParams.append("q", params.q);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/employees${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createEmployee(token, payload) {
  return request("/employees", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateEmployee(token, userId, payload) {
  return request(`/employees/${userId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteEmployee(token, userId, reassignToEmployeeId = null) {
  const qs = reassignToEmployeeId
    ? `?reassign_to_employee_id=${encodeURIComponent(reassignToEmployeeId)}`
    : "";
  return request(`/employees/${userId}${qs}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getRoles(token) {
  return request("/roles", {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createRole(token, payload) {
  return request("/roles", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function updateRole(token, roleId, payload) {
  return request(`/roles/${roleId}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function deleteRole(token, roleId) {
  return request(`/roles/${roleId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function getDepartments(token) {
  return request("/departments", {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createDepartment(token, payload) {
  return request("/departments", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function updateDepartment(token, departmentId, payload) {
  return request(`/departments/${departmentId}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function deleteDepartment(token, departmentId) {
  return request(`/departments/${departmentId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function getPricingPlans(token) {
  return request("/pricing-plans", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getPublicPricingPlans() {
  return request("/public/pricing-plans", {
    method: "GET",
  });
}

export async function getPricingPlan(token, id) {
  return request(`/pricing-plans/${id}`, {
    method: "GET",
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : {},
  });
}

export async function createPricingPlan(token, payload) {
  return request("/pricing-plans", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updatePricingPlan(token, id, payload) {
  return request(`/pricing-plans/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deletePricingPlan(token, id) {
  return request(`/pricing-plans/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getBanks(token) {
  return request("/banks", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getBank(token, id) {
  return request(`/banks/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createBank(token, payload) {
  return request("/banks", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateBank(token, id, payload) {
  return request(`/banks/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteBank(token, id) {
  return request(`/banks/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAgents(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.append("q", params.q);
  if (params.status) searchParams.append("status", params.status);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/agents${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getEmployee(token, userId) {
  return request(`/employees/${userId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getCommissionPackages(token) {
  return request("/commission/packages", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createCommissionPackage(token, payload) {
  return request("/commission/packages", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getCommissionPackage(token, packageId) {
  return request(`/commission/packages/${packageId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateCommissionPackage(token, packageId, payload) {
  return request(`/commission/packages/${packageId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteCommissionPackage(token, packageId) {
  return request(`/commission/packages/${packageId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createCommissionSlab(token, packageId, payload) {
  return request(`/commission/packages/${packageId}/slabs`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateCommissionSlab(token, packageId, slabId, payload) {
  return request(`/commission/packages/${packageId}/slabs/${slabId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteCommissionSlab(token, packageId, slabId) {
  return request(`/commission/packages/${packageId}/slabs/${slabId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getCommissionRecords(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.append("q", params.q);
  if (params.status) searchParams.append("status", params.status);
  if (Array.isArray(params.statuses) && params.statuses.length) {
    searchParams.append("statuses", params.statuses.join(","));
  }
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/commission/records${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getCommissionRecord(token, recordId) {
  return request(`/commission/records/${recordId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateCommissionRecord(token, recordId, payload) {
  return request(`/commission/records/${recordId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteCommissionRecord(token, recordId) {
  return request(`/commission/records/${recordId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createAgentRequestPublic(payload) {
  return request("/public/agent-requests", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAgentRequests(token) {
  return request("/agent-requests", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateAgentRequest(token, requestId, payload) {
  return request(`/agent-requests/${requestId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function bulkUpdateAgentRequests(token, payload) {
  return request("/agent-requests/bulk-update", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function createAgentLoan(token, payload) {
  return request("/agent/loans", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getLoans(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.append("status", params.status);
  if (params.q) searchParams.append("q", params.q);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/loans${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function exportLoans(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.append("status", params.status);
  if (params.q) searchParams.append("q", params.q);
  if (params.format) searchParams.append("format", params.format);
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  const qs = searchParams.toString();
  const { blob, filename } = await downloadFile(`/loans/export${qs ? `?${qs}` : ""}`, token);
  triggerBrowserDownload(blob, filename);
}

export async function getLoanById(token, loanId) {
  return request(`/loans/${loanId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getLoanDocuments(token, loanId) {
  return request(`/loans/${loanId}/documents`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function reviewLoanDocument(token, loanId, documentId, payload) {
  return request(`/loans/${loanId}/documents/${documentId}/review`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function createLoan(token, payload) {
  return request("/loans", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function uploadLoanDocument(token, loanId, docKey, file) {
  const formData = new FormData();
  formData.append("doc_key", docKey);
  formData.append("file", file);

  const url = `${API_BASE_URL}/loans/${loanId}/documents`;
  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  } catch (e) {
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw buildApiError(resp.status, data, `Request failed: ${resp.status}`);
  }
  return data;
}

export async function updateLoan(token, loanId, payload) {
  return request(`/loans/${loanId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function assignLoan(token, loanId, assignedEmployeeId) {
  return request(`/loans/${loanId}/assign`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ assigned_employee_id: assignedEmployeeId }),
  });
}

export async function bulkUpdateLoans(token, payload) {
  return request("/loans/bulk-update", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteLoan(token, loanId, options = {}) {
  const searchParams = new URLSearchParams();
  if (options.rollbackUploadFailure) {
    searchParams.append("rollback_upload_failure", "true");
  }
  const qs = searchParams.toString();
  return request(`/loans/${loanId}${qs ? `?${qs}` : ""}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAgentLoans(token) {
  const resp = await request("/agent/loans", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.items)) return resp.data.items;
  return [];
}

export async function getAgentLoanById(token, loanId) {
  return request(`/agent/loans/${loanId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAgentLoanDocuments(token, loanId) {
  return request(`/agent/loans/${loanId}/documents`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAgentLoanResubmissionSummary(token, loanId) {
  return request(`/agent/loans/${loanId}/resubmission-summary`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function uploadAgentLoanDocument(token, loanId, docKey, file) {
  const formData = new FormData();
  formData.append("doc_key", docKey);
  formData.append("file", file);

  const url = `${API_BASE_URL}/agent/loans/${loanId}/documents`;
  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  } catch (e) {
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw buildApiError(resp.status, data, `Request failed: ${resp.status}`);
  }
  return data;
}

async function downloadFile(path, token) {
  const url = `${API_BASE_URL}${path}`;
  let resp;
  try {
    resp = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (e) {
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  if (!resp.ok) {
    const data = await resp.json().catch(() => null);
    throw buildApiError(resp.status, data, `Request failed: ${resp.status}`);
  }

  const blob = await resp.blob();
  const disposition = resp.headers.get("content-disposition") || "";
  const match = disposition.match(/filename=\"?([^"]+)\"?/i);
  return {
    blob,
    filename: match?.[1] || "download",
  };
}

function triggerBrowserDownload(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export async function resubmitAgentLoanDocument(token, loanId, documentId, file) {
  const formData = new FormData();
  formData.append("file", file);

  const url = `${API_BASE_URL}/agent/loans/${loanId}/documents/${documentId}/resubmit`;
  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  } catch (e) {
    throw new Error(e?.message || BACKEND_UNREACHABLE_MSG);
  }

  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw buildApiError(resp.status, data, `Request failed: ${resp.status}`);
  }
  return data;
}

export async function createAgent(token, payload) {
  return request("/agents", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getAgent(token, userId) {
  return request(`/agents/${userId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateAgent(token, userId, payload) {
  return request(`/agents/${userId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteAgent(token, userId) {
  return request(`/agents/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getMyProfile(token) {
  return request("/auth/me", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function startImpersonationSession(token, targetUserId) {
  return request("/auth/impersonation/start", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ target_user_id: targetUserId }),
  });
}

export async function stopImpersonationSession(token) {
  return request("/auth/impersonation/stop", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateMyProfile(token, payload) {
  return request("/auth/me", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function changeMyPassword(token, payload) {
  return request("/auth/change-password", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getWhiteLabels(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.append("q", params.q);
  if (params.status) searchParams.append("status", params.status);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.pageSize) searchParams.append("page_size", String(params.pageSize));
  const qs = searchParams.toString();
  return request(`/white-labels${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createWhiteLabel(token, payload) {
  return request("/white-labels", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getWhiteLabelById(token, whiteLabelId) {
  return request(`/white-labels/${whiteLabelId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateWhiteLabel(token, whiteLabelId, payload) {
  return request(`/white-labels/${whiteLabelId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateWhiteLabelStatus(token, whiteLabelId, statusValue) {
  return request(`/white-labels/${whiteLabelId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ status: statusValue }),
  });
}

export async function deleteWhiteLabel(token, whiteLabelId) {
  return request(`/white-labels/${whiteLabelId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

// ── Public Razorpay ────────────────────────────────────────────────────────────

export async function createPublicCheckoutOrder(payload) {
  return request("/public/checkout/order", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function verifyRazorpayPayment(razorpay_order_id, razorpay_payment_id, razorpay_signature) {
  return request("/public/checkout/verify", {
    method: "POST",
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
    }),
  });
}

export async function createDashboardUpgradeOrder(token, payload) {
  return request("/payment/subscription/upgrade/order", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function verifyDashboardUpgradePayment(token, razorpay_order_id, razorpay_payment_id, razorpay_signature) {
  return request("/payment/subscription/upgrade/verify", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
    }),
  });
}
